/**
 * Текст, который берётся в буфер нажатием.
 *
 * Половина работы вилочника — перенос: название матча в поиск конторы, сумма в
 * форму ставки, исход глазами. Владелец про это: «любое нажатие на название
 * пари или на ставку должно копироваться, и должно быть интуитивно понятно»
 * (08.08). До этого копировать умела ровно одна кнопка, спрятанная в подписи
 * ноги, а название матча в раскрытой карточке не показывалось вовсе — его
 * приходилось выцеливать в свёрнутой строке.
 *
 * Приглашение — пунктир под текстом и курсор «копировать»: два признака, оба
 * привычные, ни один не занимает места. Ответ обязателен: буфер молчалив, и без
 * подтверждения человек жмёт второй раз, не зная, сработало ли.
 */

import { useEffect, useRef, useState } from 'react';

/** Положить в буфер. `false` — ни clipboard, ни запасной путь не сработали. */
export async function copy(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Запасной путь ниже: политика браузера часто режет clipboard API.
  }
  const exec = document.execCommand?.bind(document);
  if (exec === undefined) return false;
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.append(area);
    area.select();
    const ok = exec('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Короткое «в буфере» / «не скопировалось» — буфер молчалив. */
export function useCopyFlash(): {
  took: boolean;
  fail: boolean;
  flash: string;
  take: (text: string) => void;
} {
  const [took, setTook] = useState(false);
  const [fail, setFail] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current !== null && clearTimeout(timer.current)), []);

  const take = (text: string): void => {
    void copy(text).then((ok) => {
      setTook(ok);
      setFail(!ok);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setTook(false);
        setFail(false);
      }, 1400);
    });
  };

  return {
    took,
    fail,
    flash: `${took ? ' took' : ''}${fail ? ' fail' : ''}`,
    take,
  };
}

/** Тихая иконка копирования: текст не выглядит ссылкой. */
export function Clip({
  text,
  what,
  className = 'nx-clip',
}: {
  text: string;
  what: string;
  className?: string;
}): React.ReactElement {
  const { fail, took, flash, take } = useCopyFlash();
  return (
    <button
      type="button"
      className={`${className}${flash}`}
      title={fail ? `не скопировалось: ${what}` : took ? `${what} в буфере` : `скопировать ${what}`}
      aria-label={`скопировать ${what}`}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        take(text);
      }}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
        <path d="M10.5 3.5v-1h-8v8h1" />
      </svg>
    </button>
  );
}

export function Grab({
  text,
  children,
  as = 'span',
  what,
}: {
  /** Что уедет в буфер. Не обязано совпадать с показанным: сумма копируется без знака валюты. */
  text: string;
  children: React.ReactNode;
  as?: 'span' | 'b';
  /** Чем это назвать в подсказке: «название матча», «сумму», «исход». */
  what: string;
}): React.ReactElement {
  const { fail, flash, take } = useCopyFlash();
  const Tag = as;
  return (
    <Tag
      className={`nx-grab${flash}`}
      role="button"
      tabIndex={0}
      title={fail ? `не скопировалось: ${what}` : `нажмите, чтобы скопировать ${what}`}
      onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
      onClick={(event: React.MouseEvent) => {
        // Карточка живёт внутри строки ленты: без этого нажатие на исход
        // сложило бы карточку, а человек ждал буфер.
        event.stopPropagation();
        take(text);
      }}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        take(text);
      }}
    >
      {children}
    </Tag>
  );
}
